'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Image from 'next/image';
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from '@headlessui/react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { usePermissionsQuery } from '@/lib/platform/hooks/queries/use-permissions-query';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useClubsQuery } from '@/lib/platform/hooks/queries/use-clubs-query';
import { useDemoMode } from '@/lib/platform/hooks/use-demo-mode';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { useNegotiatorMode } from '@/lib/platform/contexts/negotiator-mode-context';
import { canViewDashboard } from '@/lib/platform/utils/permissions-helpers';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import Spinner from '@/components/platform/ui/Spinner';
import OrganizationIcon from './OrganizationIcon';
import NavigationTree from './NavigationTree';
import {
  navigationTree,
  filterNavigationTree,
} from '@/lib/platform/navigation/get-first-accessible-page';
import { logger } from '@/lib/platform/utils/logger';
import { useAlias } from '@/lib/platform/hooks/use-alias';

interface TeamItem {
  id: string;
  name: string;
  initial: string;
  logoUrl?: string | null;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface TailwindSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export default function TailwindSidebar({ sidebarOpen, setSidebarOpen }: TailwindSidebarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { logoUrl, organization } = useOrganization();
  const { data: permissions } = usePermissionsQuery();
  const { darkMode } = useDarkMode();
  const { data: clubs = [] } = useClubsQuery();
  const { isDemo, blockIfDemo } = useDemoMode();
  const { features } = useEntitlements();
  const { isNegotiatorMode } = useNegotiatorMode();
  const [isNavigatingToOnboarding, setIsNavigatingToOnboarding] = useState(false);
  const [switchingTeamId, setSwitchingTeamId] = useState<string | null>(null);
  const [negotiatorDisplay, setNegotiatorDisplay] = useState<{
    mode: 'emoji' | 'color' | 'free' | 'temp';
    value: string;
    color: string;
  }>({ mode: 'emoji', value: '😊', color: '#A3E635' });

  const defaultCoverageLabels: {
    [key: string]: { smilie: string; color: string; free: string; temp: string };
  } = useMemo(
    () => ({
      'Negative (Loss)': { smilie: '💀', color: '#000000', free: '⚠️', temp: '💀' },
      '0-5%': { smilie: '🥶', color: '#7F1D1D', free: 'T', temp: '🥶' },
      '5-10%': { smilie: '😖', color: '#991B1B', free: 'S', temp: '❄️' },
      '10-15%': { smilie: '😫', color: '#B91C1C', free: 'R', temp: '🧊' },
      '15-20%': { smilie: '😩', color: '#DC2626', free: 'Q', temp: '🧥' },
      '20-25%': { smilie: '😞', color: '#EF4444', free: 'P', temp: '🌁' },
      '25-30%': { smilie: '😔', color: '#F87171', free: 'O', temp: '🌫️' },
      '30-35%': { smilie: '😕', color: '#FB923C', free: 'N', temp: '☁️' },
      '35-40%': { smilie: '😐', color: '#FBBF24', free: 'M', temp: '🌥️' },
      '40-45%': { smilie: '😑', color: '#FCD34D', free: 'L', temp: '🌤️' },
      '45-50%': { smilie: '🤨', color: '#FDE047', free: 'K', temp: '🙂' },
      '50-55%': { smilie: '🙂', color: '#BEF264', free: 'J', temp: '🌞' },
      '55-60%': { smilie: '😊', color: '#A3E635', free: 'I', temp: '🔅' },
      '60-65%': { smilie: '😀', color: '#84CC16', free: 'H', temp: '🔆' },
      '65-70%': { smilie: '😁', color: '#65A30D', free: 'G', temp: '☀️' },
      '70-75%': { smilie: '😆', color: '#4D7C0F', free: 'F', temp: '🔥' },
      '75-80%': { smilie: '🤩', color: '#3F6212', free: 'E', temp: '♨️' },
      '80-85%': { smilie: '🤗', color: '#365314', free: 'D', temp: '🌋' },
      '85-90%': { smilie: '😍', color: '#1A4D2E', free: 'C', temp: '🌶️' },
      '90-95%': { smilie: '😸', color: '#166534', free: 'B', temp: '🌡️' },
      '95-100%': { smilie: '🥳', color: '#14532D', free: 'A', temp: '🥵' },
    }),
    []
  );

  const [coverageLabels, setCoverageLabels] = useState<{
    [key: string]: { smilie: string; color: string; free: string; temp: string };
  }>(defaultCoverageLabels);
  const [currentCoveragePercentage, setCurrentCoveragePercentage] = useState<number | null>(null);

  // Load negotiator display mode from localStorage. Deliberately a mount effect:
  // reading localStorage during render (lazy useState) would cause an SSR
  // hydration mismatch, so the one-time post-hydration setState is intentional.
  useEffect(() => {
    const savedMode = localStorage.getItem('negotiatorDisplayMode');
    if (savedMode && ['emoji', 'temp', 'color', 'free'].includes(savedMode)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNegotiatorDisplay((prev) => ({
        ...prev,
        mode: savedMode as 'emoji' | 'temp' | 'color' | 'free',
      }));
    }
  }, []);

  // Helper function to find the coverage range for a given percentage
  const getCoverageRange = (percentage: number): string => {
    // Negative coverage = loss (cost exceeds sales price)
    if (percentage < 0) return 'Negative (Loss)';
    if (percentage < 5) return '0-5%';
    if (percentage < 10) return '5-10%';
    if (percentage < 15) return '10-15%';
    if (percentage < 20) return '15-20%';
    if (percentage < 25) return '20-25%';
    if (percentage < 30) return '25-30%';
    if (percentage < 35) return '30-35%';
    if (percentage < 40) return '35-40%';
    if (percentage < 45) return '40-45%';
    if (percentage < 50) return '45-50%';
    if (percentage < 55) return '50-55%';
    if (percentage < 60) return '55-60%';
    if (percentage < 65) return '60-65%';
    if (percentage < 70) return '65-70%';
    if (percentage < 75) return '70-75%';
    if (percentage < 80) return '75-80%';
    if (percentage < 85) return '80-85%';
    if (percentage < 90) return '85-90%';
    if (percentage < 95) return '90-95%';
    return '95-100%';
  };

  // Update display based on current coverage percentage and mode
  const updateDisplayFromCoverage = useCallback(
    (
      percentage: number,
      mode: 'emoji' | 'color' | 'free' | 'temp',
      labels: { [key: string]: { smilie: string; color: string; free: string; temp?: string } }
    ) => {
      const range = getCoverageRange(percentage);
      // Use provided labels or fall back to defaults
      const labelsToUse = Object.keys(labels).length > 0 ? labels : defaultCoverageLabels;
      const rangeData = labelsToUse[range] ||
        defaultCoverageLabels[range] || { smilie: '🥶', color: '#7F1D1D', free: 'A', temp: '🥶' };
      let value = '';
      if (mode === 'emoji') value = rangeData.smilie;
      else if (mode === 'free') value = rangeData.free;
      else if (mode === 'temp') value = rangeData.temp || rangeData.smilie;
      setNegotiatorDisplay({
        mode,
        value,
        color: rangeData.color,
      });
    },
    [defaultCoverageLabels]
  );

  // For demo users, always show demo logo
  const displayLogoUrl = isDemo ? '/DemoOrganisation.png' : logoUrl;

  // Check if current page is in sponsorships area
  const isSponsorshipsArea = pathname?.startsWith('/products/sponsorships/');

  // Fetch sponsorship settings when in sponsorships area and negotiator mode is on
  useEffect(() => {
    const fetchSettings = async () => {
      if (!isNegotiatorMode || !isSponsorshipsArea || !organization?.id) return;

      try {
        const { fetchWithBypass } = await import('@/lib/platform/utils/fetch');
        const response = await fetchWithBypass(
          `/api/products/sponsorships/settings?organizationId=${organization.id}`
        );
        if (response.ok) {
          const data = await response.json();
          const displayMode = data.settings?.display_mode || 'emoji';
          const labels = data.settings?.negotiator_coverage_labels || {};
          // Store labels for later use
          setCoverageLabels(labels);
          // If we have a current coverage percentage, use it; otherwise hide indicator
          if (currentCoveragePercentage !== null) {
            updateDisplayFromCoverage(currentCoveragePercentage, displayMode, labels);
          } else {
            // No active package - clear display
            setNegotiatorDisplay({
              mode: displayMode,
              value: '',
              color: '',
            });
          }
        }
      } catch {
        // Silently fail - use defaults
      }

      // Request current coverage from package builder (in case it loaded before negotiator mode was ready)
      window.dispatchEvent(new CustomEvent('request-coverage-percentage'));
    };

    fetchSettings();
  }, [
    isNegotiatorMode,
    isSponsorshipsArea,
    organization?.id,
    currentCoveragePercentage,
    updateDisplayFromCoverage,
  ]);

  // Listen for display mode changes from header - separate effect to avoid closure issues
  useEffect(() => {
    const handleDisplayModeChange = (event: CustomEvent) => {
      const { mode } = event.detail;
      // Get current coverage from localStorage or state
      const currentPercentage = currentCoveragePercentage;

      if (currentPercentage !== null) {
        // Update display with new mode and current coverage
        const range = getCoverageRange(currentPercentage);
        const labelsToUse =
          Object.keys(coverageLabels).length > 0 ? coverageLabels : defaultCoverageLabels;
        const rangeData = labelsToUse[range] ||
          defaultCoverageLabels[range] || { smilie: '🥶', color: '#7F1D1D', free: 'A', temp: '🥶' };

        let value = '';
        if (mode === 'emoji') value = rangeData.smilie;
        else if (mode === 'free') value = rangeData.free;
        else if (mode === 'temp') value = rangeData.temp || rangeData.smilie;

        setNegotiatorDisplay({
          mode,
          value,
          color: rangeData.color,
        });
      } else {
        // Just update mode if no coverage data
        setNegotiatorDisplay((prev) => ({ ...prev, mode }));
      }
    };

    window.addEventListener(
      'negotiator-display-mode-changed',
      handleDisplayModeChange as EventListener
    );
    return () => {
      window.removeEventListener(
        'negotiator-display-mode-changed',
        handleDisplayModeChange as EventListener
      );
    };
  }, [currentCoveragePercentage, coverageLabels, defaultCoverageLabels]);

  // Listen for coverage percentage changes from package builder
  useEffect(() => {
    const handleCoverageChange = (event: CustomEvent) => {
      const { percentage } = event.detail;
      setCurrentCoveragePercentage(percentage);
      if (percentage !== null) {
        updateDisplayFromCoverage(percentage, negotiatorDisplay.mode, coverageLabels);
      }
    };

    window.addEventListener('coverage-percentage-changed', handleCoverageChange as EventListener);
    return () => {
      window.removeEventListener(
        'coverage-percentage-changed',
        handleCoverageChange as EventListener
      );
    };
  }, [negotiatorDisplay.mode, coverageLabels, updateDisplayFromCoverage]);

  // Clear coverage when leaving sponsorships area (adjust-state-during-render
  // pattern, https://react.dev/learn/you-might-not-need-an-effect)
  const [prevIsSponsorshipsArea, setPrevIsSponsorshipsArea] = useState(isSponsorshipsArea);
  if (prevIsSponsorshipsArea !== isSponsorshipsArea) {
    setPrevIsSponsorshipsArea(isSponsorshipsArea);
    if (!isSponsorshipsArea) {
      setCurrentCoveragePercentage(null);
    }
  }

  // Transform clubs to team items format
  const teams: TeamItem[] = clubs.map((club) => ({
    id: club.id,
    name: club.name,
    initial: club.name.charAt(0).toUpperCase(),
    logoUrl: isDemo ? '/DemoOrganisation.png' : club.logo_url,
  }));

  const { getLabel } = useAlias();

  // Filter navigation tree based on permissions and plan features
  // Then apply label aliases for sponsorship terminology
  const filtered = filterNavigationTree(navigationTree, permissions, features);
  const filteredNavigation = filtered.map((node) => {
    if (node.id === 'sponsorships') {
      return {
        ...node,
        name: getLabel('sponsorship_module').toUpperCase(),
        children: node.children?.map((child) => {
          if (child.id === 'sponsors') return { ...child, name: getLabel('sponsor_plural') };
          if (child.id === 'packages') return { ...child, name: getLabel('package_plural') };
          return child;
        }),
      };
    }
    return node;
  });

  const switchTeam = async (teamId: string) => {
    logger.log('[TailwindSidebar] switchTeam called with teamId:', teamId);
    setSwitchingTeamId(teamId);

    // CRITICAL: Update localStorage DIRECTLY before reload
    // Do NOT rely on setSelectedOrganization hook - it might be async
    if (typeof window !== 'undefined') {
      localStorage.setItem('selected_organization_id', teamId);
      logger.log('[TailwindSidebar] localStorage updated directly to:', teamId);
      logger.log('[TailwindSidebar] Verify:', localStorage.getItem('selected_organization_id'));
    }

    // Small delay to ensure localStorage write completes
    await new Promise((resolve) => setTimeout(resolve, 50));

    logger.log('[TailwindSidebar] Forcing page reload...');

    // DRASTIC FIX: Force full page reload to ensure new organization loads
    window.location.reload();
  };

  // Prefetch critical routes for faster navigation
  useEffect(() => {
    // Prefetch most commonly accessed routes
    router.prefetch('/valuations');
    router.prefetch('/settings');
    // Only prefetch dashboard if user has access
    if (canViewDashboard(permissions)) {
      router.prefetch('/dashboard');
    }
  }, [router, permissions]);

  return (
    <>
      {/* Mobile sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop
          transition
          className={`fixed inset-0 backdrop-blur-[2px] transition-opacity duration-300 ease-linear data-closed:opacity-0 ${
            darkMode ? 'bg-gray-900/30' : 'bg-white/30'
          }`}
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className={`size-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  />
                </button>
              </div>
            </TransitionChild>

            {/* Sidebar component */}
            <div
              className={`relative flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4 ring-1 ${darkMode ? 'bg-gray-900 ring-white/10' : 'bg-white ring-gray-200'}`}
            >
              <div className="flex h-16 shrink-0 items-center">
                {displayLogoUrl ? (
                  <Image
                    src={displayLogoUrl}
                    alt="Club Logo"
                    width={32}
                    height={32}
                    priority
                    className="h-8 w-auto"
                  />
                ) : (
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${darkMode ? '' : 'bg-white'}`}
                  >
                    <svg
                      viewBox="0 0 1024 1024"
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                    >
                      <polygon
                        fill={darkMode ? '#FFFFFF' : '#374151'}
                        points="596.21 113.59 862.65 113.59 1011.98 368.32 515.69 952.44 751.39 368.32 751.39 368.32 596.21 113.59"
                      />
                      <polygon
                        fill={darkMode ? '#FFFFFF' : '#374151'}
                        points="427.79 114.04 161.35 114.04 12.02 368.77 508.31 952.89 272.61 368.77 272.61 368.77 427.79 114.04"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <nav className="flex flex-1 flex-col">
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <NavigationTree
                      data={filteredNavigation}
                      className="-mx-2"
                      onNavigate={(href) => {
                        router.push(href);
                        setSidebarOpen(false); // Close mobile menu on navigation
                      }}
                    />
                  </li>
                  {teams.length > 0 && (
                    <li>
                      <div
                        className={`flex items-center justify-between text-xs/6 font-semibold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                      >
                        <span>Organisations ({teams.length})</span>
                        <TooltipPrimitive.Provider delayDuration={300}>
                          <TooltipPrimitive.Root>
                            <TooltipPrimitive.Trigger asChild>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!blockIfDemo()) {
                                    setIsNavigatingToOnboarding(true);
                                    router.push('/welcome?new=true');
                                  }
                                }}
                                disabled={isNavigatingToOnboarding}
                                aria-label="Add new organization"
                                className={`rounded p-1 transition-colors ${
                                  isNavigatingToOnboarding
                                    ? 'cursor-not-allowed opacity-50'
                                    : darkMode
                                      ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                      : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                                }`}
                              >
                                {isNavigatingToOnboarding ? (
                                  <Spinner size="sm" darkMode={darkMode} />
                                ) : (
                                  <PlusIcon className="size-4" />
                                )}
                              </button>
                            </TooltipPrimitive.Trigger>
                            <TooltipPrimitive.Portal>
                              <TooltipPrimitive.Content
                                className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                                  darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                                }`}
                                sideOffset={5}
                              >
                                Create new organisation
                                <TooltipPrimitive.Arrow
                                  className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                                />
                              </TooltipPrimitive.Content>
                            </TooltipPrimitive.Portal>
                          </TooltipPrimitive.Root>
                        </TooltipPrimitive.Provider>
                      </div>
                      <ul role="list" className="-mx-2 mt-2 space-y-1">
                        {teams.map((team) => {
                          const current = team.id === organization?.id;
                          const isSwitching = switchingTeamId === team.id;
                          return (
                            <li key={team.id}>
                              <button
                                onClick={() => {
                                  logger.log(
                                    '[TailwindSidebar] Button clicked! Team:',
                                    team.name,
                                    'ID:',
                                    team.id,
                                    'Current:',
                                    current
                                  );
                                  switchTeam(team.id);
                                }}
                                disabled={isSwitching}
                                className={classNames(
                                  current
                                    ? darkMode
                                      ? 'bg-blue-900/20 text-blue-400'
                                      : 'bg-blue-50/50 text-blue-800'
                                    : darkMode
                                      ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                                  'group flex w-full gap-x-3 rounded-md p-2 text-sm/6 font-medium',
                                  isSwitching ? 'cursor-not-allowed opacity-60' : ''
                                )}
                              >
                                <OrganizationIcon
                                  logoUrl={team.logoUrl}
                                  isActive={current}
                                  isLoading={isSwitching}
                                  darkMode={darkMode}
                                  initial={team.initial}
                                />
                                <span className="truncate">{team.name}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </li>
                  )}
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <div
        className={`hidden h-full flex-col gap-y-5 overflow-y-auto border-r px-6 pb-4 lg:flex ${darkMode ? 'border-white/10 bg-gray-900' : 'border-gray-200 bg-white'}`}
      >
        <div className="flex h-16 shrink-0 items-center">
          {displayLogoUrl ? (
            <Image
              src={displayLogoUrl}
              alt="Club Logo"
              width={32}
              height={32}
              priority
              className="h-8 w-auto"
            />
          ) : (
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${darkMode ? '' : 'bg-white'}`}
            >
              <svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                <polygon
                  fill={darkMode ? '#FFFFFF' : '#374151'}
                  points="596.21 113.59 862.65 113.59 1011.98 368.32 515.69 952.44 751.39 368.32 751.39 368.32 596.21 113.59"
                />
                <polygon
                  fill={darkMode ? '#FFFFFF' : '#374151'}
                  points="427.79 114.04 161.35 114.04 12.02 368.77 508.31 952.89 272.61 368.77 272.61 368.77 427.79 114.04"
                />
              </svg>
            </div>
          )}
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <li>
              <NavigationTree data={filteredNavigation} className="-mx-2" />
            </li>
            {teams.length > 0 && (
              <li>
                <div
                  className={`flex items-center justify-between text-xs/6 font-semibold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  <span>Organisations ({teams.length})</span>
                  <TooltipPrimitive.Provider delayDuration={300}>
                    <TooltipPrimitive.Root>
                      <TooltipPrimitive.Trigger asChild>
                        <button
                          type="button"
                          onClick={() => {
                            if (!blockIfDemo()) {
                              setIsNavigatingToOnboarding(true);
                              router.push('/welcome?new=true');
                            }
                          }}
                          disabled={isNavigatingToOnboarding}
                          aria-label="Add new organization"
                          className={`rounded p-1 transition-colors ${
                            isNavigatingToOnboarding
                              ? 'cursor-not-allowed opacity-50'
                              : darkMode
                                ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                          }`}
                        >
                          {isNavigatingToOnboarding ? (
                            <Spinner size="sm" darkMode={darkMode} />
                          ) : (
                            <PlusIcon className="size-4" />
                          )}
                        </button>
                      </TooltipPrimitive.Trigger>
                      <TooltipPrimitive.Portal>
                        <TooltipPrimitive.Content
                          className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                            darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                          }`}
                          sideOffset={5}
                        >
                          Create new organisation
                          <TooltipPrimitive.Arrow
                            className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                          />
                        </TooltipPrimitive.Content>
                      </TooltipPrimitive.Portal>
                    </TooltipPrimitive.Root>
                  </TooltipPrimitive.Provider>
                </div>
                <ul role="list" className="-mx-2 mt-2 space-y-1">
                  {teams.map((team) => {
                    const current = team.id === organization?.id;
                    const isSwitching = switchingTeamId === team.id;
                    return (
                      <li key={team.id}>
                        <button
                          onClick={() => {
                            logger.log(
                              '[TailwindSidebar] Mobile button clicked! Team:',
                              team.name,
                              'ID:',
                              team.id,
                              'Current:',
                              current
                            );
                            switchTeam(team.id);
                          }}
                          disabled={isSwitching}
                          className={classNames(
                            current
                              ? darkMode
                                ? 'bg-blue-900/20 text-blue-400'
                                : 'bg-blue-50/50 text-blue-800'
                              : darkMode
                                ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
                            'group flex w-full gap-x-3 rounded-md p-2 text-sm/6 font-medium',
                            isSwitching ? 'cursor-not-allowed opacity-60' : ''
                          )}
                        >
                          <OrganizationIcon
                            logoUrl={team.logoUrl}
                            isActive={current}
                            isLoading={isSwitching}
                            darkMode={darkMode}
                            initial={team.initial}
                          />
                          <span className="truncate">{team.name}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </>
  );
}
